import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Linkedin, CreditCard, Clock, CheckCircle, BadgeCheck, Globe, Users } from "lucide-react";
import HavnsLogo from "../components/HavnsLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Application = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    linkedin_url: "",
    statement_of_intent: "",
    profession: "",
    expertise: "",
    hobbies: "",
    travel_bucket_list: "",
    bio: "",
    location: "",
  });

  // Determine initial step based on user status
  useEffect(() => {
    if (user) {
      if (user.application_status === "approved" && user.payment_status === "completed") {
        navigate("/dashboard");
      } else if (user.application_status === "approved" && user.payment_status !== "completed") {
        setCurrentStep(3); // Payment step
      } else if (user.linkedin_url) {
        setCurrentStep(2); // Already submitted, waiting for approval
      }
      
      // Pre-fill form with existing data
      setFormData({
        linkedin_url: user.linkedin_url || "",
        statement_of_intent: user.statement_of_intent || "",
        profession: user.profession || "",
        expertise: user.expertise?.join(", ") || "",
        hobbies: user.hobbies?.join(", ") || "",
        travel_bucket_list: user.travel_bucket_list?.join(", ") || "",
        bio: user.bio || "",
        location: user.location || "",
      });
    }
    
    // Check if redirected to payment step
    if (location.state?.step === "payment") {
      setCurrentStep(3);
    }
  }, [user, navigate, location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitApplication = async () => {
    setLoading(true);
    try {
      const payload = {
        linkedin_url: formData.linkedin_url,
        statement_of_intent: formData.statement_of_intent,
        profession: formData.profession,
        expertise: formData.expertise.split(",").map((s) => s.trim()).filter(Boolean),
        hobbies: formData.hobbies.split(",").map((s) => s.trim()).filter(Boolean),
        travel_bucket_list: formData.travel_bucket_list.split(",").map((s) => s.trim()).filter(Boolean),
        bio: formData.bio,
        location: formData.location,
      };

      await axios.post(`${API}/application/submit`, payload, { withCredentials: true });
      await refreshUser();
      toast.success("Application submitted successfully!");
      setCurrentStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      // Create order
      const orderResponse = await axios.post(`${API}/payment/create-order`, {}, { withCredentials: true });
      const order = orderResponse.data;

      // For MVP with placeholder keys, simulate payment
      if (order.key_id === "rzp_test_placeholder") {
        // Simulate successful payment
        await axios.post(`${API}/payment/verify`, {
          order_id: order.order_id,
          payment_id: `pay_demo_${Date.now()}`,
        }, { withCredentials: true });

        await refreshUser();
        toast.success("Payment successful! Welcome to Havns.");
        navigate("/dashboard");
        return;
      }

      // Real Razorpay integration (when keys are provided)
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Havns",
        description: "Monthly Membership - ₹999",
        order_id: order.order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API}/payment/verify`, {
              order_id: order.order_id,
              payment_id: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            }, { withCredentials: true });

            await refreshUser();
            toast.success("Payment successful! Welcome to Havns.");
            navigate("/dashboard");
          } catch (error) {
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: order.user_name,
          email: order.user_email,
        },
        theme: {
          color: "#1A1A1A",
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: "Application", icon: Linkedin },
    { number: 2, title: "Review", icon: Clock },
    { number: 3, title: "Payment", icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-ivory">
      {/* Header */}
      <header className="border-b border-border bg-ivory/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container-luxury flex items-center justify-between h-20">
          <HavnsLogo className="text-2xl" linkTo="/" />
          <Button variant="ghost" onClick={logout} className="btn-ghost" data-testid="logout-btn">
            Sign Out
          </Button>
        </div>
      </header>

      <main className="container-luxury py-16">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-16">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <div
                className={`flex items-center gap-3 px-4 py-2 ${
                  currentStep >= step.number
                    ? "text-charcoal"
                    : "text-muted-foreground"
                }`}
              >
                <div
                  className={`w-10 h-10 flex items-center justify-center border ${
                    currentStep > step.number
                      ? "bg-gold border-gold text-charcoal"
                      : currentStep === step.number
                      ? "border-charcoal"
                      : "border-muted"
                  }`}
                >
                  {currentStep > step.number ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span className="font-mono text-sm">{step.number}</span>
                  )}
                </div>
                <span className="text-caption hidden md:block">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-12 h-px ${currentStep > step.number ? "bg-gold" : "bg-border"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="max-w-2xl mx-auto">
          {/* Step 1: Application Form */}
          {currentStep === 1 && (
            <div className="animate-fade-in">
              <div className="text-center mb-12">
                <h1 className="text-h1 text-charcoal mb-4">Membership Application</h1>
                <p className="text-body-lg">
                  Tell us about yourself. We review every application to ensure
                  our community remains high-quality and aligned.
                </p>
              </div>

              <div className="space-y-8">
                <div className="form-group">
                  <label className="form-label">LinkedIn Profile URL *</label>
                  <Input
                    name="linkedin_url"
                    value={formData.linkedin_url}
                    onChange={handleInputChange}
                    placeholder="https://linkedin.com/in/yourprofile"
                    className="input-luxury"
                    data-testid="input-linkedin"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Your Profession *</label>
                  <Input
                    name="profession"
                    value={formData.profession}
                    onChange={handleInputChange}
                    placeholder="e.g., Product Designer at Google"
                    className="input-luxury"
                    data-testid="input-profession"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Location</label>
                  <Input
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="e.g., Mumbai, India"
                    className="input-luxury"
                    data-testid="input-location"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Statement of Intent *</label>
                  <Textarea
                    name="statement_of_intent"
                    value={formData.statement_of_intent}
                    onChange={handleInputChange}
                    placeholder="Why do you want to join Havns? What does being single-by-choice mean to you?"
                    className="input-luxury min-h-[120px] resize-none"
                    data-testid="input-intent"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Areas of Expertise (comma-separated)</label>
                  <Input
                    name="expertise"
                    value={formData.expertise}
                    onChange={handleInputChange}
                    placeholder="e.g., UX Design, Photography, Finance"
                    className="input-luxury"
                    data-testid="input-expertise"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Hobbies & Interests (comma-separated)</label>
                  <Input
                    name="hobbies"
                    value={formData.hobbies}
                    onChange={handleInputChange}
                    placeholder="e.g., Hiking, Wine Tasting, Reading"
                    className="input-luxury"
                    data-testid="input-hobbies"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Travel Bucket List (comma-separated)</label>
                  <Input
                    name="travel_bucket_list"
                    value={formData.travel_bucket_list}
                    onChange={handleInputChange}
                    placeholder="e.g., Japan, Iceland, Peru"
                    className="input-luxury"
                    data-testid="input-bucket-list"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Short Bio</label>
                  <Textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    placeholder="A brief introduction about yourself..."
                    className="input-luxury min-h-[100px] resize-none"
                    data-testid="input-bio"
                  />
                </div>

                <div className="pt-8">
                  <Button
                    onClick={handleSubmitApplication}
                    disabled={!formData.linkedin_url || !formData.statement_of_intent || !formData.profession || loading}
                    className="btn-primary w-full inline-flex items-center justify-center gap-3"
                    data-testid="submit-application-btn"
                  >
                    {loading ? "Submitting..." : "Submit Application"}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Waiting for Approval */}
          {currentStep === 2 && user?.application_status === "pending" && (
            <div className="animate-fade-in text-center py-16">
              <Clock className="w-16 h-16 text-gold mx-auto mb-8" strokeWidth={1} />
              <h1 className="text-h1 text-charcoal mb-4">Application Under Review</h1>
              <p className="text-body-lg max-w-md mx-auto mb-8">
                Thank you for applying. Our team reviews each application
                personally to maintain community quality. You'll hear from us soon.
              </p>
              <Button
                onClick={() => refreshUser()}
                variant="outline"
                className="btn-secondary"
                data-testid="refresh-status-btn"
              >
                Check Status
              </Button>
            </div>
          )}

          {/* Step 2: Rejected */}
          {currentStep === 2 && user?.application_status === "rejected" && (
            <div className="animate-fade-in text-center py-16">
              <div className="w-16 h-16 border border-destructive text-destructive flex items-center justify-center mx-auto mb-8">
                <span className="text-2xl">×</span>
              </div>
              <h1 className="text-h1 text-charcoal mb-4">Application Not Approved</h1>
              <p className="text-body-lg max-w-md mx-auto mb-8">
                Unfortunately, your application wasn't approved at this time.
                You may reapply after 30 days with updated information.
              </p>
              <Button
                onClick={() => setCurrentStep(1)}
                variant="outline"
                className="btn-secondary"
                data-testid="reapply-btn"
              >
                Update Application
              </Button>
            </div>
          )}

          {/* Step 3: Payment */}
          {(currentStep === 3 || user?.application_status === "approved") && user?.payment_status !== "completed" && (
            <div className="animate-fade-in py-8">
              <div className="text-center mb-12">
                <CheckCircle className="w-16 h-16 text-gold mx-auto mb-6" strokeWidth={1} />
                <h1 className="text-h1 text-charcoal mb-4">Application Approved!</h1>
                <p className="text-body-lg max-w-md mx-auto">
                  You're in! Complete your membership to unlock full access.
                </p>
              </div>

              {/* Benefits Reminder */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
                <div className="bg-white border border-border p-6 text-center">
                  <BadgeCheck className="w-6 h-6 text-gold mx-auto mb-3" />
                  <h4 className="font-medium text-charcoal mb-2">Verified Network</h4>
                  <p className="text-sm text-muted-foreground">
                    Connect with Aadhaar-verified professionals only
                  </p>
                </div>
                <div className="bg-white border border-border p-6 text-center">
                  <Globe className="w-6 h-6 text-gold mx-auto mb-3" />
                  <h4 className="font-medium text-charcoal mb-2">Solo Journeys</h4>
                  <p className="text-sm text-muted-foreground">
                    Post & join travel plans with verified companions
                  </p>
                </div>
                <div className="bg-white border border-border p-6 text-center">
                  <Users className="w-6 h-6 text-gold mx-auto mb-3" />
                  <h4 className="font-medium text-charcoal mb-2">Local Meetups</h4>
                  <p className="text-sm text-muted-foreground">
                    Attend curated events in your city
                  </p>
                </div>
              </div>

              {/* Pricing Card */}
              <div className="bg-white border border-border p-8 max-w-md mx-auto mb-8">
                <div className="text-center mb-6">
                  <p className="text-caption mb-2">Monthly Membership</p>
                  <p className="text-h1 text-charcoal">₹999</p>
                </div>
                
                {/* Transparency Note */}
                <div className="bg-ivory p-4 mb-6">
                  <p className="text-sm text-muted-foreground">
                    This fee keeps out bots and casual users, ensuring only serious 
                    professionals in our platonic community.
                  </p>
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-gold" />
                    <span>Unlimited journey posts & joins</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-gold" />
                    <span>Access to all local meetups</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-gold" />
                    <span>Direct messaging with members</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-gold" />
                    <span>Verified Professional badge</span>
                  </li>
                </ul>

                <Button
                  onClick={handlePayment}
                  disabled={loading}
                  className="btn-primary w-full inline-flex items-center justify-center gap-3"
                  data-testid="pay-now-btn"
                >
                  {loading ? "Processing..." : "Pay & Enter Community"}
                  <CreditCard className="w-4 h-4" />
                </Button>
              </div>

              <p className="text-caption text-center">
                Secure payment powered by Razorpay. Cancel anytime.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Application;
